import { useEffect, useMemo, useRef, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Bank, CheckCircle, CircleNotch, Plus, UploadSimple, WarningCircle } from "@phosphor-icons/react";
import { RowanTerm } from "@/components/RowanTerm";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const STATUS = {
  open: { label: "Open", bg: "#EAF0F6", color: "#3A5A7A" },
  partial: { label: "Partial", bg: "#FFF4D8", color: "#8A6A3B" },
  overdue: { label: "Overdue", bg: "#FDECE8", color: "#B23B1F" },
  paid: { label: "Paid", bg: "var(--bk-success-bg)", color: "#2F6B4F" },
  void: { label: "Voided", bg: "var(--bk-page)", color: "var(--bk-text-2)" },
};

const billLine = (bill) => {
  if (bill.display_status === "void") return "Voided · not owed";
  if (bill.display_status === "paid") return `Paid in full${bill.last_payment_at ? ` · ${bill.last_payment_at}` : ""}`;
  const base = `${money(bill.balance_due)} open`;
  if (bill.days_past_due > 0) return `${base} · ${bill.days_past_due} day${bill.days_past_due === 1 ? "" : "s"} late`;
  if (bill.due_in_days !== null && bill.due_in_days !== undefined && bill.due_in_days <= 7) return `${base} · due in ${bill.due_in_days} day${bill.due_in_days === 1 ? "" : "s"}`;
  return base;
};

function SummaryCard({ label, value, accent, testId, term }) {
  return (
    <div className="bk-card p-5 bk-lift" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>{term ? <RowanTerm term={term} size={12}>{label}</RowanTerm> : label}</p>
      <p className="font-display text-2xl font-bold mt-2" style={{ color: accent }}>{money(value)}</p>
    </div>
  );
}

export function Payables({ businessId }) {
  const [summary, setSummary] = useState(null);
  const [bills, setBills] = useState([]);
  const [cats, setCats] = useState({ expense: [] });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [recording, setRecording] = useState("");
  const [paymentModal, setPaymentModal] = useState({ open: false, bill: null, amount: "", date: today() });
  const [showSettled, setShowSettled] = useState(false);
  const openBills = useMemo(() => bills.filter((b) => b.display_status !== "paid" && b.display_status !== "void"), [bills]);
  const settledBills = useMemo(() => bills.filter((b) => b.display_status === "paid" || b.display_status === "void"), [bills]);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ vendor_name: "", vendor_email: "", bill_number: "", issue_date: today(), due_date: "", tax_rate: "", notes: "", expense_category: "Other", payment_account: "Bank", document_path: "" });
  const [items, setItems] = useState([{ description: "", quantity: 1, rate: "" }]);

  const load = async () => {
    try {
      const [sumRes, billsRes, catRes] = await Promise.all([
        api.get(`/ap/summary?business_id=${businessId}`),
        api.get(`/ap/bills?business_id=${businessId}`),
        api.get(`/businesses/${businessId}/categories`),
      ]);
      setSummary(sumRes.data);
      setBills(billsRes.data || []);
      setCats(catRes.data || { expense: [] });
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const subtotal = useMemo(() => items.reduce((acc, item) => acc + (Number(item.quantity) || 0) * (Number(item.rate) || 0), 0), [items]);
  const taxAmt = (subtotal * (Number(form.tax_rate) || 0)) / 100;
  const total = subtotal + taxAmt;

  const setItem = (idx, key, value) => setItems((rows) => rows.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  const addItem = () => setItems((rows) => [...rows, { description: "", quantity: 1, rate: "" }]);
  const removeItem = (idx) => setItems((rows) => rows.filter((_, i) => i !== idx));

  const createBill = async () => {
    if (!form.vendor_name.trim()) return toast.error("Add a vendor name");
    const clean = items.filter((item) => item.description.trim() && Number(item.rate) > 0);
    if (clean.length === 0) return toast.error("Add at least one bill line");
    setSaving(true);
    try {
      await api.post("/ap/bills", {
        business_id: businessId,
        vendor_name: form.vendor_name,
        vendor_email: form.vendor_email,
        bill_number: form.bill_number,
        issue_date: form.issue_date,
        due_date: form.due_date,
        tax_rate: Number(form.tax_rate) || 0,
        notes: form.notes,
        expense_category: form.expense_category,
        payment_account: form.payment_account,
        document_path: form.document_path,
        items: clean.map((item) => ({ description: item.description, quantity: Number(item.quantity) || 1, rate: Number(item.rate) || 0 })),
      });
      toast.success("Bill saved to A/P");
      setForm({ vendor_name: "", vendor_email: "", bill_number: "", issue_date: today(), due_date: "", tax_rate: "", notes: "", expense_category: "Other", payment_account: "Bank", document_path: "" });
      setItems([{ description: "", quantity: 1, rate: "" }]);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save that bill.");
    } finally {
      setSaving(false);
    }
  };

  const onScanFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    const fd = new FormData();
    fd.append("business_id", businessId);
    fd.append("file", file);
    try {
      const { data } = await api.post("/ap/bills/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({
        vendor_name: data.vendor_name || "",
        vendor_email: data.vendor_email || "",
        bill_number: data.bill_number || "",
        issue_date: data.issue_date || today(),
        due_date: data.due_date || "",
        tax_rate: data.tax_rate || "",
        notes: data.notes || "",
        expense_category: data.expense_category || "Other",
        payment_account: "Bank",
        document_path: data.document_path || "",
      });
      setItems((data.items || []).map((item) => ({ description: item.description, quantity: item.quantity || 1, rate: item.rate || item.amount || 0 })));
      setShowForm(true);
      toast.success("Bill scanned — review and save it.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't scan that bill.");
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openPayment = (bill) => setPaymentModal({ open: true, bill, amount: String(Number(bill.balance_due || 0).toFixed(2)), date: today() });
  const recordPayment = async () => {
    if (!paymentModal.bill) return;
    setRecording(paymentModal.bill.id);
    try {
      await api.patch(`/ap/bills/${paymentModal.bill.id}`, { payment_amount: Number(paymentModal.amount) || 0, payment_date: paymentModal.date });
      toast.success("Bill payment recorded");
      setPaymentModal({ open: false, bill: null, amount: "", date: today() });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't record that payment.");
    } finally {
      setRecording("");
    }
  };

  return (
    <div className="space-y-6" data-testid="ap-panel">
      <div className="rounded-[28px] p-7 relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(255,249,240,0.96), rgba(255,255,255,0.98))", border: "1px solid rgba(229,231,235,0.85)" }} data-testid="ap-hero">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Accounts Payable</p>
            <h2 className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>Track vendor bills before they track you.</h2>
            <p className="text-sm mt-3" style={{ color: "var(--bk-text-2)" }} data-testid="ap-rowan-message">{summary?.rowan_message || "Rowan is keeping one eye on due dates and the other on your cash."}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf" onChange={onScanFile} className="hidden" data-testid="ap-scan-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="ap-scan-button" className="bk-btn-secondary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-70" style={{ color: "var(--bk-text)" }}>
              {scanning ? <><CircleNotch size={16} className="animate-spin" /> Scanning…</> : <><UploadSimple size={16} weight="bold" /> Scan vendor bill</>}
            </button>
            <button onClick={() => setShowForm((v) => !v)} data-testid="ap-new-bill-button" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold">
              <Plus size={16} weight="bold" /> New bill
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="ap-summary-grid">
        <SummaryCard label="Outstanding" term="ap" value={summary?.outstanding_total} accent="var(--bk-accent)" testId="ap-summary-outstanding" />
        <SummaryCard label="Overdue" term="overdue" value={summary?.overdue_total} accent="#B23B1F" testId="ap-summary-overdue" />
        <SummaryCard label="Due in 7 days" term="due_soon" value={summary?.due_7_days_total} accent="#8A6A3B" testId="ap-summary-due-soon" />
        <SummaryCard label="61+ days" term="aging" value={(summary?.bucket_61_90 || 0) + (summary?.bucket_90_plus || 0)} accent="var(--bk-success)" testId="ap-summary-aged" />
      </div>

      {showForm && (
        <div className="bk-card p-6 space-y-4" data-testid="ap-form" style={{ background: "linear-gradient(180deg, rgba(255,249,240,0.52), #fff)" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={form.vendor_name} onChange={(e) => setForm((f) => ({ ...f, vendor_name: e.target.value }))} placeholder="Vendor name" data-testid="ap-vendor-name-input" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input value={form.vendor_email} onChange={(e) => setForm((f) => ({ ...f, vendor_email: e.target.value }))} placeholder="Vendor email (optional)" data-testid="ap-vendor-email-input" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input value={form.bill_number} onChange={(e) => setForm((f) => ({ ...f, bill_number: e.target.value }))} placeholder="Vendor bill #" data-testid="ap-bill-number-input" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <select value={form.expense_category} onChange={(e) => setForm((f) => ({ ...f, expense_category: e.target.value }))} data-testid="ap-expense-category-select" className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
              {(cats.expense || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Issue date<DateInput value={form.issue_date} onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))} data-testid="ap-issue-date-input" className="w-full mt-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} /></label>
            <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Due date<DateInput value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="ap-due-date-input" className="w-full mt-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} /></label>
          </div>
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`ap-line-item-${idx}`}>
                <input value={item.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Description" data-testid={`ap-line-item-description-${idx}`} className="flex-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <input type="number" value={item.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} placeholder="Qty" data-testid={`ap-line-item-quantity-${idx}`} className="w-16 rounded-md border px-2 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <input type="number" value={item.rate} onChange={(e) => setItem(idx, "rate", e.target.value)} placeholder="Rate" data-testid={`ap-line-item-rate-${idx}`} className="w-24 rounded-md border px-2 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                <div className="w-24 text-right text-sm font-semibold" data-testid={`ap-line-item-total-${idx}`} style={{ color: "var(--bk-text)" }}>{money((Number(item.quantity) || 0) * (Number(item.rate) || 0))}</div>
                {items.length > 1 && <button onClick={() => removeItem(idx)} data-testid={`ap-line-item-remove-${idx}`} className="text-sm font-semibold px-2 py-1" style={{ color: "#B23B1F" }}>Remove</button>}
              </div>
            ))}
            <button onClick={addItem} data-testid="ap-add-line-item-button" className="text-sm font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--bk-accent)" }}><Plus size={14} weight="bold" /> Add line</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
            <div className="space-y-3">
              <label className="text-xs block" style={{ color: "var(--bk-text-2)" }}>Tax rate (%)<input type="number" value={form.tax_rate} onChange={(e) => setForm((f) => ({ ...f, tax_rate: e.target.value }))} data-testid="ap-tax-rate-input" className="w-28 mt-1 rounded-md border px-3 py-2 text-sm outline-none block" style={{ borderColor: "var(--bk-border)" }} /></label>
              <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Notes (optional)" data-testid="ap-notes-input" className="w-full rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            </div>
            <div className="text-right text-sm" style={{ color: "var(--bk-text)" }}>
              <div>Subtotal: <span className="font-semibold">{money(subtotal)}</span></div>
              <div>Tax: <span className="font-semibold">{money(taxAmt)}</span></div>
              <div className="font-display text-lg font-bold" data-testid="ap-form-total">Total: {money(total)}</div>
            </div>
          </div>
          {form.document_path && <div className="rounded-xl px-3 py-2 text-xs" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }} data-testid="ap-scanned-document-pill">Scanned source attached</div>}
          <div className="flex justify-end">
            <button onClick={createBill} disabled={saving} data-testid="ap-save-bill-button" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold disabled:opacity-70">
              {saving ? <><CircleNotch size={16} className="animate-spin" /> Saving…</> : <>Save bill</>}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1.25fr_0.75fr] gap-6">
        <div className="bk-card p-0 overflow-hidden" data-testid="ap-bill-list-card">
          <div className="p-5 border-b" style={{ borderColor: "var(--bk-border)" }}>
            <h3 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Bills & due dates</h3>
            <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="ap-open-bills-recap">{openBills.length} open bill{openBills.length === 1 ? "" : "s"} · {money(openBills.reduce((a, b) => a + Number(b.balance_due || 0), 0))} owed{settledBills.length > 0 ? ` · ${settledBills.length} paid or voided` : ""}</p>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
            {openBills.length === 0 && settledBills.length === 0 ? (
              <div className="p-10 text-center bk-empty-state" data-testid="ap-empty-state">
                <Bank size={40} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
                <p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>No bills yet.</p>
                <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Scan or add a vendor bill to track what you owe.</p>
              </div>
            ) : [...openBills, ...(showSettled ? settledBills : [])].map((bill) => {
              const badge = STATUS[bill.display_status] || STATUS.open;
              const isOpen = bill.display_status !== "paid" && bill.display_status !== "void";
              return (
                <div key={bill.id} className="p-5 flex flex-wrap items-start justify-between gap-4" data-testid={`ap-bill-${bill.id}`}>
                  <div className="min-w-[240px] flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>{bill.bill_number || "Bill"}</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" data-testid={`ap-bill-status-${bill.id}`} style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
                      {isOpen && <span className="text-xs font-bold px-2 py-0.5 rounded-full" data-testid={`ap-bill-aging-${bill.id}`} style={{ background: "var(--bk-page)", color: "var(--bk-accent)" }}>{bill.aging_bucket === "current" ? "Current" : bill.aging_bucket.replace("_", "-")}</span>}
                    </div>
                    <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{bill.vendor_name} · due {bill.due_date || "—"}</p>
                    <p className="text-xs mt-1" style={{ color: bill.display_status === "overdue" ? "#B23B1F" : "var(--bk-text-2)" }} data-testid={`ap-bill-balance-${bill.id}`}>{billLine(bill)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {isOpen && bill.balance_due > 0 && <button onClick={() => openPayment(bill)} data-testid={`ap-record-payment-button-${bill.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}><CheckCircle size={13} weight="bold" /> Record payment</button>}
                  </div>
                </div>
              );
            })}
            {settledBills.length > 0 && (
              <button onClick={() => setShowSettled((v) => !v)} data-testid="ap-toggle-settled" className="w-full p-3 text-xs font-semibold" style={{ color: "var(--bk-accent)" }}>
                {showSettled ? "Hide" : "Show"} {settledBills.length} paid or voided bill{settledBills.length === 1 ? "" : "s"}
              </button>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bk-card p-5" data-testid="ap-aging-buckets-card">
            <h3 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Payables aging</h3>
            <div className="space-y-3 mt-4 text-sm">
              {[
                ["Current", summary?.current_total || 0],
                ["1-30", summary?.bucket_1_30 || 0],
                ["31-60", summary?.bucket_31_60 || 0],
                ["61-90", summary?.bucket_61_90 || 0],
                ["90+", summary?.bucket_90_plus || 0],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between" data-testid={`ap-bucket-${label}`}>
                  <span style={{ color: "var(--bk-text-2)" }}>{label} days</span>
                  <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{money(value)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bk-card p-5" data-testid="ap-action-card">
            <h3 className="font-display text-lg font-bold flex items-center gap-2" style={{ color: "var(--bk-text)" }}><WarningCircle size={18} weight="duotone" color="var(--bk-accent)" /> Cash-preservation note</h3>
            <p className="text-sm mt-3" style={{ color: "var(--bk-text-2)" }}>
              {summary?.overdue_total > 0
                ? `Clear the overdue ${money(summary.overdue_total)} first, then work forward from the bills due in the next 7 days.`
                : `You've got ${money(summary?.due_7_days_total || 0)} coming due in the next week. Prioritize the bills that keep operations moving.`}
            </p>
          </div>
        </div>
      </div>

      <Dialog open={paymentModal.open} onOpenChange={(open) => setPaymentModal((m) => ({ ...m, open }))}>
        <DialogContent data-testid="ap-payment-modal">
          <DialogHeader><DialogTitle style={{ color: "var(--bk-text)" }}>Record vendor payment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Amount<input type="number" value={paymentModal.amount} onChange={(e) => setPaymentModal((m) => ({ ...m, amount: e.target.value }))} data-testid="ap-payment-amount-input" className="w-full mt-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} /></label>
            <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Payment date<DateInput value={paymentModal.date} onChange={(e) => setPaymentModal((m) => ({ ...m, date: e.target.value }))} data-testid="ap-payment-date-input" className="w-full mt-1 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} /></label>
            <button onClick={recordPayment} disabled={recording === paymentModal.bill?.id} data-testid="ap-payment-submit-button" className="bk-btn-primary w-full py-2.5 text-sm font-semibold disabled:opacity-60">{recording === paymentModal.bill?.id ? "Saving…" : "Save payment"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}